import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import useWishlistStore from '../store/wishlistStore';
import { toast } from 'sonner';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

const Wishlist = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore(state => state.addItem);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const removeFromWishlist = useWishlistStore(state => state.removeFromWishlist);
  const fetchWishlist = useWishlistStore(state => state.fetchWishlist);
  const wishlistIds = useWishlistStore(state => state.wishlistIds);

  useEffect(() => {
    fetchWishlistData();
  }, [isAuthenticated, wishlistIds.join(',')]);

  const fetchWishlistData = async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        // Eingeloggt → Backend liefert direkt die Produkte
        const wishlistResponse = await axiosInstance.get('/wishlist');
        if (wishlistResponse.data.products) {
          setProducts(wishlistResponse.data.products);
        }
        await fetchWishlist();
      } else {
        // Gast → Produkte über die öffentliche Produkt-API einzeln laden
        if (wishlistIds.length === 0) {
          setProducts([]);
          setLoading(false);
          return;
        }
        const results = await Promise.all(
          wishlistIds.map((id) =>
            axiosInstance.get(`/products/${id}`).then(r => r.data).catch(() => null),
          ),
        );
        setProducts(results.filter(Boolean));
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Fehler beim Laden der Favoritenliste');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    const success = await removeFromWishlist(productId);
    if (success) {
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Von Favoritenliste entfernt');
    } else {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleAddToCart = (product) => {
    if (!addItem(product)) return;
    toast.success(`${product.name} wurde zum Warenkorb hinzugefügt`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="wishlist-title">Meine Favoritenliste</h1>
        
        {products.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="empty-wishlist-title">
              Ihre Favoritenliste ist leer
            </h2>
            <p className="text-gray-600 mb-6">Speichern Sie Ihre Lieblingsprodukte hier</p>
            <Link to="/products">
              <Button data-testid="browse-products" className="rounded-none text-white" >Produkte durchsuchen</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="wishlist-grid">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow"
                data-testid={`wishlist-item-${product.id}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square relative overflow-hidden">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={imgVariant(product.images[0], { w: 400 })}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <ShoppingBag className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                </Link>
                
                <div className="p-4">
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 hover:text-blue-600">
                      {product.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-lg font-bold text-gray-900">{formatEUR(product.price)}</p>
                    {product.is_handmade && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                        Handgemacht
                      </span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 rounded-none text-white"
                      disabled={!(product.stock > 0)}
                      data-testid={`add-to-cart-${product.id}`}
                      
                    >
                      {!(product.stock > 0) ? 'Ausverkauft' : 'In den Warenkorb'}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveFromWishlist(product.id)}
                      data-testid={`remove-${product.id}`}
                      className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;